import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Modal } from '@/components/ui/Modal'
import { useState } from 'react'
import {
  useFormContext,
  type FieldValues,
  type Path,
  type RegisterOptions,
} from 'react-hook-form'
import styles from './Select.module.scss'

interface SelectOption {
  text: string
  value: string
}

interface SelectProps<T extends FieldValues> {
  name: Path<T>
  options: SelectOption[]
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  multiple?: boolean
  modalTitle?: string
  modalSubtitle?: string
  validation?: RegisterOptions<T>
  onChange?: (value: string | string[]) => void
  testId?: string
}

export const Select = <T extends FieldValues>({
  name,
  options,
  label,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  multiple = false,
  modalTitle,
  modalSubtitle,
  validation,
  onChange,
  testId,
}: SelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelection, setTempSelection] = useState<string | string[]>(
    multiple ? [] : ''
  )

  const {
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext<T>()

  const value = watch(name)
  const error = errors[name]
  const errorMessage = error?.message as string | undefined

  const containerClassNames = [styles.container, !label && styles.noLabel]
    .filter(Boolean)
    .join(' ')

  const triggerClassNames = [
    styles.trigger,
    error && styles.triggerError,
    readOnly && styles.triggerReadOnly,
  ]
    .filter(Boolean)
    .join(' ')

  const getDisplayValue = () => {
    if (multiple) {
      const selectedValues = (value as string[]) || []
      if (selectedValues.length === 0) return null
      const selectedTexts = options
        .filter((opt) => selectedValues.includes(opt.value))
        .map((opt) => opt.text)
      return selectedTexts.join(', ')
    } else {
      const selectedOption = options.find((opt) => opt.value === value)
      return selectedOption?.text || null
    }
  }

  const displayValue = getDisplayValue()

  const handleOpen = () => {
    if (disabled || readOnly) return
    setTempSelection(value || (multiple ? [] : ''))
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleOptionClick = (optionValue: string) => {
    if (multiple) {
      const current = tempSelection as string[]
      if (current.includes(optionValue)) {
        setTempSelection(current.filter((v) => v !== optionValue))
      } else {
        setTempSelection([...current, optionValue])
      }
    } else {
      setTempSelection(optionValue)
    }
  }

  const handleConfirm = () => {
    setValue(name, tempSelection as T[typeof name], { shouldValidate: true })
    if (required || validation) {
      trigger(name)
    }
    onChange?.(tempSelection)
    setIsOpen(false)
  }

  const isOptionSelected = (optionValue: string) => {
    if (multiple) {
      return (tempSelection as string[]).includes(optionValue)
    }
    return tempSelection === optionValue
  }

  return (
    <div className={containerClassNames}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <button
        type="button"
        className={triggerClassNames}
        onClick={handleOpen}
        disabled={disabled}
        data-testid={testId || `select-${name}`}
      >
        {displayValue ? (
          <span>{displayValue}</span>
        ) : (
          <span className={styles.placeholder}>
            {placeholder || 'Select...'}
          </span>
        )}
        <Icon name="chevron-down" size={16} className={styles.chevron} />
      </button>

      {errorMessage && (
        <span
          className={styles.errorMessage}
          data-testid={testId ? `${testId}-error` : `select-${name}-error`}
        >
          {errorMessage}
        </span>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={modalTitle || label || 'Select'}
        subtitle={modalSubtitle}
        buttonLayout="column"
        buttons={[
          <Button
            key="confirm"
            text="Confirm"
            variant="primary"
            onClick={handleConfirm}
          />,
          <Button
            key="back"
            text="Go Back"
            variant="default"
            onClick={handleClose}
          />,
        ]}
      >
        <div
          className={styles.options}
          data-testid={testId ? `${testId}-options` : `select-${name}-options`}
        >
          {options.map((option) => {
            const selected = isOptionSelected(option.value)
            return (
              <div
                key={option.value}
                className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                onClick={() => handleOptionClick(option.value)}
                data-testid={
                  testId
                    ? `${testId}-option-${option.value}`
                    : `select-${name}-option-${option.value}`
                }
              >
                <span className={styles.optionText}>{option.text}</span>
                <div className={`${styles.radio}`}>
                  {selected && <div className={styles.radioInner} />}
                </div>
              </div>
            )
          })}
        </div>
      </Modal>
    </div>
  )
}
